import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic, TeamPublic } from '@mondy/shared';
import { api } from '@/api';
import {
  buildGroups,
  GroupCard,
  StandingsGlossary,
  ThirdPlaceLegend,
  ThirdPlaceRanking,
} from '@/components/GroupStandings';
import { KnockoutBracket } from '@/components/KnockoutBracket';
import { Spinner } from '@/components/Spinner';
import { TeamSearch } from '@/components/TeamSearch';
import { cn } from '@/lib/cn';

interface TeamsResponse {
  teams: TeamPublic[];
}

interface MatchListResponse {
  matches: MatchPublic[];
}

export function TeamsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'knockout' ? 'knockout' : 'group';

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
  });

  // All matches power both the group standings and the knockout projection.
  // Short stale-time so live-game impact shows up without a manual refresh.
  const matchesQuery = useQuery({
    queryKey: ['matches', 'all'],
    queryFn: () => api.get<MatchListResponse>('/api/matches'),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });

  const teams = teamsQuery.data?.teams ?? [];
  const matches = matchesQuery.data?.matches ?? [];

  const groups = useMemo(
    () => (teamsQuery.data ? buildGroups(teams, matches) : []),
    [teamsQuery.data, matchesQuery.data],
  );

  const setTab = (next: 'group' | 'knockout') =>
    setParams(next === 'knockout' ? { tab: 'knockout' } : {}, { replace: true });

  return (
    <>
      {/* Lift the whole page-head stacking context above the sibling groups
          grid (both are `main.app-main > *` at z-index 1) so the search
          dropdown, which is trapped inside this context, can overlay them. */}
      <div className="page-head" style={{ position: 'relative', zIndex: 30 }}>
        <div className="page-title">
          <div className="k">48 teams · 12 groups · June 11 → July 19, 2026</div>
          <h1>
            The <span className="emph">teams</span>.
          </h1>
        </div>
        <TeamSearch teams={teamsQuery.data?.teams ?? []} />
      </div>

      <div className="pills" role="tablist" aria-label="Stage">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'group'}
          className={cn('pill', tab === 'group' && 'active')}
          onClick={() => setTab('group')}
        >
          Group stage
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'knockout'}
          className={cn('pill', tab === 'knockout' && 'active')}
          onClick={() => setTab('knockout')}
        >
          Knockout stage
        </button>
      </div>

      {teamsQuery.isLoading || matchesQuery.isLoading ? (
        <Spinner fullScreen label="Loading teams" />
      ) : teamsQuery.isError ? (
        <p
          style={{
            padding: '48px 0',
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--danger)',
          }}
        >
          Couldn't load teams.
        </p>
      ) : tab === 'knockout' ? (
        <KnockoutBracket groups={groups} matches={matches} />
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <ThirdPlaceRanking groups={groups} matches={matches} />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 14,
            }}
          >
            {groups.map((g) => (
              <GroupCard key={g.group} {...g} />
            ))}
          </div>
          <div
            style={{
              marginTop: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <ThirdPlaceLegend />
            <StandingsGlossary />
          </div>
        </>
      )}
    </>
  );
}
